import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AdminReviewSessionViewDto,
  AnyoneLinkStateDto,
  ApproveFlyerResponseDto,
  AudienceWithUrlDto,
  CreateFlyerReviewAudienceDto,
  RegenerateAudienceTokenDto,
  RegenerateAudienceTokenResponseDto,
  ReopenFlyerResponseDto,
  RestorePermissionsDto,
  SetAnyoneLinkDto,
  UpdateFlyerReviewAudienceDto,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../locales/messages";
import { toastSuccess } from "../../../utils/pageUtil/toast";
import { reviewApiService } from "../../../utils/api";
import { handleReviewError } from "./errorHandling";
import { invalidateFlyerMetaCaches } from "./invalidateReviewCache";
import { reviewKeys } from "./queryKeys";

export const useFlyerReview = (
  flyerId: string | undefined,
  authHeaders: { headers: { Authorization: string } },
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const auth = authHeaders.headers.Authorization;
  const reqOptions = useMemo(
    () => ({ headers: { Authorization: auth } }),
    [auth],
  );

  const bootstrapQuery = useQuery({
    queryKey: reviewKeys.bootstrap(flyerId ?? ""),
    queryFn: ({ signal }) =>
      reviewApiService.gfFlyerReviewAdminControllerGetReviewBootstrap(
        flyerId as string,
        { headers: { Authorization: auth }, signal },
      ),
    enabled: !!flyerId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const bootstrap = bootstrapQuery.data as
    | AdminReviewSessionViewDto
    | undefined;
  // Mirror the previous semantics: the command-bar skeleton showed during the
  // initial load AND every refetch, so map to `isFetching` (not `isLoading`).
  const bootstrapLoading = bootstrapQuery.isFetching;

  /**
   * Invalidate the bootstrap query. react-query dedupes concurrent
   * invalidations, so a user mutation immediately followed by the matching
   * socket event no longer double-fetches the session.
   */
  const refetchBootstrap = useCallback(async () => {
    if (!flyerId) return;
    await queryClient.invalidateQueries({
      queryKey: reviewKeys.bootstrap(flyerId),
    });
  }, [flyerId, queryClient]);

  const inviteAudience = useCallback(
    async (
      body: CreateFlyerReviewAudienceDto,
    ): Promise<AudienceWithUrlDto | undefined> => {
      if (!flyerId) return;
      try {
        const result =
          await reviewApiService.gfFlyerReviewAdminControllerInviteAudience(
            body,
            flyerId,
            reqOptions,
          );
        await refetchBootstrap();
        return result;
      } catch (error) {
        await handleReviewError(error, t);
        return undefined;
      }
    },
    [flyerId, refetchBootstrap, reqOptions, t],
  );

  const updateAudience = useCallback(
    async (audienceId: string, body: UpdateFlyerReviewAudienceDto) => {
      if (!flyerId) return;
      try {
        await reviewApiService.gfFlyerReviewAdminControllerUpdateAudience(
          body,
          flyerId,
          audienceId,
          reqOptions,
        );
        await refetchBootstrap();
        return true;
      } catch (error) {
        await handleReviewError(error, t);
        return false;
      }
    },
    [flyerId, refetchBootstrap, reqOptions, t],
  );

  const regenerateAudienceToken = useCallback(
    async (
      audienceId: string,
      body: RegenerateAudienceTokenDto,
    ): Promise<RegenerateAudienceTokenResponseDto | undefined> => {
      if (!flyerId) return;
      try {
        const result =
          await reviewApiService.gfFlyerReviewAdminControllerRegenerateAudienceToken(
            body,
            flyerId,
            audienceId,
            reqOptions,
          );
        await refetchBootstrap();
        return result;
      } catch (error) {
        await handleReviewError(error, t);
        return undefined;
      }
    },
    [flyerId, refetchBootstrap, reqOptions, t],
  );

  const revokeAudience = useCallback(
    async (audienceId: string) => {
      if (!flyerId) return;
      try {
        await reviewApiService.gfFlyerReviewAdminControllerRevokeAudience(
          flyerId,
          audienceId,
          reqOptions,
        );
        await refetchBootstrap();
        return true;
      } catch (error) {
        await handleReviewError(error, t);
        return false;
      }
    },
    [flyerId, refetchBootstrap, reqOptions, t],
  );

  const setAnyoneLink = useCallback(
    async (body: SetAnyoneLinkDto): Promise<AnyoneLinkStateDto | undefined> => {
      if (!flyerId) return;
      try {
        const result =
          await reviewApiService.gfFlyerReviewAdminControllerSetAnyoneLink(
            body,
            flyerId,
            reqOptions,
          );
        await refetchBootstrap();
        return result;
      } catch (error) {
        await handleReviewError(error, t);
        return undefined;
      }
    },
    [flyerId, refetchBootstrap, reqOptions, t],
  );

  const approve = useCallback(
    async (
      publishDate?: string,
    ): Promise<ApproveFlyerResponseDto | undefined> => {
      if (!flyerId) return;
      try {
        const result =
          await reviewApiService.gfFlyerReviewAdminControllerApproveFlyer(
            { publishDate },
            flyerId,
            reqOptions,
          );
        await invalidateFlyerMetaCaches(queryClient, flyerId);
        toastSuccess(
          t(messages.Reviewers_Moved_To_View_Only(), {
            count: result.demotedAudienceIds?.length || 0,
          }),
        );
        return result;
      } catch (error) {
        await handleReviewError(error, t);
        return undefined;
      }
    },
    [flyerId, queryClient, reqOptions, t],
  );

  const reopen = useCallback(async (): Promise<
    ReopenFlyerResponseDto | undefined
  > => {
    if (!flyerId) return;
    try {
      const result =
        await reviewApiService.gfFlyerReviewAdminControllerReopenFlyer(
          flyerId,
          reqOptions,
        );
      await invalidateFlyerMetaCaches(queryClient, flyerId);
      return result;
    } catch (error) {
      await handleReviewError(error, t);
      return undefined;
    }
  }, [flyerId, queryClient, reqOptions, t]);

  const restorePermissions = useCallback(
    async (body: RestorePermissionsDto) => {
      if (!flyerId) return;
      try {
        await reviewApiService.gfFlyerReviewAdminControllerRestorePermissions(
          body,
          flyerId,
          reqOptions,
        );
        await refetchBootstrap();
        return true;
      } catch (error) {
        await handleReviewError(error, t);
        return false;
      }
    },
    [flyerId, refetchBootstrap, reqOptions, t],
  );

  return {
    bootstrap,
    bootstrapLoading,
    refetchBootstrap,
    inviteAudience,
    updateAudience,
    regenerateAudienceToken,
    revokeAudience,
    setAnyoneLink,
    approve,
    reopen,
    restorePermissions,
    reqOptions,
  };
};
